import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg, str } from "@lit/localize";
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { when } from "lit/directives/when.js";
import style from './timetable-entry.scss';
import a11yStyle from '../a11y/styles.scss';
import "../dialog";
import "../icon/usos-icon";

/**
 * Component for a timetable entry tile
 * When any of the dialog-* attributes is set, clicking an entry opens dialog with extra information
 *
 * @slot info - slot for base information presented on a tile
 * @slot time - slot for time information presented on a tile
 *
 * @slot dialog-info - slot for information presented in a dialog: general info
 * @slot dialog-event - slot for information presented in a dialog: entry occurrences
 * @slot dialog-person - slot for information presented in a dialog: lecturers
 * @slot dialog-place - slot for information presented in a dialog: entry place
 */

@customElement('timetable-entry')
@localized()
export class TimetableEntry extends LitElement {
    static override styles = [a11yStyle, style];

    /**
     * Show mini timetable, to be used e.g. as a thumbnail
     * managed by containing usos-timetable
     */
    @property({ type: Boolean, reflect: true }) mini = false;

    /**
     * Should this entry be highlighted (font-weight and border) on timetable?
     */
    @property({ type: Boolean, reflect: true }) highlighted = false;

    /** Title of the tile and dialog */
    @property() name = '';

    /** Item ID presented in tile's title */
    @property({ attribute: 'name-id' }) nameId = '';

    /** Number from 1 to 8 to choose tile color */
    @property({ type: Number, reflect: true }) color = 1;

    /** Whether the entry has at least one dialog slot filled, so we can show a dialog. */
    @state() private _hasDialog = false;

    constructor() {
        super();
        const internals = this.attachInternals();
        internals.role = 'listitem';
    }

    override render() {
        const unHTMLedName = (() => {
            const el = document.createElement('div');
            el.innerHTML = this.name;
            return el.innerText + (this.nameId ? ` [${this.nameId}]` : '');
        })();
        if (this.mini) return html`<div><div id='info'><slot name='info'></slot></div></div>`;

        return html`
        <div role=${this._hasDialog ? 'button' : nothing}
            tabindex=${this._hasDialog ? '0' : nothing} aria-describedby=${this._hasDialog ? 'dod-info' : nothing}
            @click=${this._onClick} @keypress=${this._onKeyPress} >
            <div id='przedmiot' title=${unHTMLedName}>
                ${unsafeHTML(this.name)}
                ${when(this.nameId, () => html`<span class='sr-only'>[${this.nameId}]</span>`)}
            </div>
            <div id='info'>
                <slot name='info'></slot>
            </div>
            <div id='time'>
                <slot name='time'></slot>
            </div>
            ${when(this._hasDialog, () => html`<div id="dod-info" class='sr-only'>${msg(str`Kliknij, aby wyświetlić okienko z dodatkowymi informacjami`)}</div>`)}
        </div>
        <usos-dialog>
            <span slot='title'>${unsafeHTML(this.name)}</span>
            <div>
                <div id="details">
                    <usos-icon icon-name="info" icon-type="outlined" style="display: none" .label=${msg(str`Informacje`)}></usos-icon>
                    <slot name='dialog-info' @slotchange=${this._dialogSlotChange}></slot>
                    <usos-icon icon-name="event" icon-type="outlined" style='display: none' .label=${msg(str`Termin`)}></usos-icon>
                    <slot name='dialog-event' @slotchange=${this._dialogSlotChange}></slot>
                    <usos-icon icon-name="person" style='display: none' .label=${msg(str`Prowadzący`)}></usos-icon>
                    <slot name='dialog-person' @slotchange=${this._dialogSlotChange}></slot>
                    <usos-icon icon-name="place" style='display: none' .label=${msg(str`Miejsce`)}></usos-icon>
                    <slot name='dialog-place' @slotchange=${this._dialogSlotChange}></slot>
                </div>
            </div>
        </usos-dialog>
        `;
    }

    private _dialogSlotChange(evt: Event): void {
        function _isSlotFilled(slot: HTMLSlotElement): boolean {
            return slot.assignedNodes().length > 0;
        }
        // hide / show usos-icon depending on whether dialog information slot is used or not
        const slot: HTMLSlotElement = evt.target as HTMLSlotElement;
        (slot.previousElementSibling! as HTMLElement).style.display = _isSlotFilled(slot) ? '' : 'none';
        this._hasDialog = Array.from(this.shadowRoot!.querySelectorAll<HTMLSlotElement>('slot[name^="dialog-"]')).some(_isSlotFilled);
    }

    private _onClick() {
        const dialog = this.shadowRoot!.querySelector('usos-dialog')!;
        if (dialog.isOpened()) return;
        if (this._hasDialog) dialog.open();
    }

    private _onKeyPress(evt: KeyboardEvent) {
        if (evt.key == 'Enter') this._onClick();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'timetable-entry': TimetableEntry
    }
}
